import clsx from 'clsx'
import includes from 'lodash/includes'
import map from 'lodash/map'
import React, { useCallback, useContext, useEffect, useRef } from 'react'

import { ProductContext } from '@Components/Product/Context'
import ArrowPagination from '@Components/Product/Slider/Components/ArrowPagination/ArrowPagination'
import { ImageHorizontal } from '@Components/Product/Slider/Components/Image/ImageHorizontal'
import { ControllerContext } from '@Components/Product/Slider/Context/ControllerContext'
import { SliderContext } from '@Components/Product/Slider/Context/SliderContext'
import { DragNext } from '@Components/Product/Slider/gestures/dragNext'
import { VideoHorizontal } from '@Components/Product/Slider/Video'
import { VimeoHorizontal } from '@Components/Product/Slider/VideoVimeo'
import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './Horizontal.module.scss'

const SWIPE_UP_ANIMATION_DURATION = 500

const HorizontalSlider = ({
  autoHeight,
  children,
  className,
}: {
  autoHeight?: boolean
  children?: React.ReactNode
  className?: string
}) => {
  const productVideoAutoplay = useSplitTest('product_video_autoplay_v1', 'b')

  const { disableDrag, isDragDisabled } = useContext(ControllerContext)
  const {
    activeSlide,
    goToNextSlide,
    goToPreviousSlide,
    isFullscreen,
    isSwipedUp,
    setSwipeUp,
    slides,
    toggleFullscreen,
  } = useContext(SliderContext)

  const product = useContext(ProductContext)

  const horizontalRef = useRef<HTMLUListElement>(null)
  DragNext(horizontalRef, goToNextSlide, goToPreviousSlide, activeSlide.key, 'x')

  const updateToggles = useCallback(() => {
    if (isSwipedUp) {
      const html = (document.getElementsByTagName('BODY') as HTMLCollectionOf<HTMLElement>)[0]
      html.style.overflow = ''
      html.style.position = ''

      const timeout = setTimeout(() => {
        setSwipeUp(false)
        disableDrag(false)
      }, SWIPE_UP_ANIMATION_DURATION)

      return () => clearTimeout(timeout)
    }

    return null
  }, [disableDrag, isSwipedUp, setSwipeUp])

  useEffect(() => {
    updateToggles()
  }, [isSwipedUp, updateToggles])

  useEffect(() => {
    const onKeyUp = (e) => {
      if (e.key === 'ArrowRight' || e.keyCode === 39) {
        goToNextSlide(true)
      }
      if (e.key === 'ArrowLeft' || e.keyCode === 37) {
        goToPreviousSlide(true)
      }
      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
        toggleFullscreen()
      }
    }
    if (isFullscreen) {
      window.addEventListener('keyup', onKeyUp)
      return () => window.removeEventListener('keyup', onKeyUp)
    }
    return () => window.removeEventListener('keyup', onKeyUp)
  }, [goToNextSlide, goToPreviousSlide, isFullscreen, toggleFullscreen])

  const touchAction = isFullscreen ? 'none' : 'pan-y'

  const handleDoubleClick = () => {
    if (!isFullscreen) {
      toggleFullscreen()
    }
  }

  const content = ref => (
    <>
      {children}

      {!isDragDisabled && (
        <ArrowPagination
          onClickLeft={() => goToPreviousSlide(true)}
          onClickRight={() => goToNextSlide(true)}
        />
      )}
      <span style={{ paddingTop: '100%', display: 'block' }} />
      <ul
        ref={slides.length === 1 ? undefined : ref}
        style={{
          display: 'flex',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          listStyle: 'none',
          padding: 0,
          margin: 0,
          touchAction,
          transform: `translateX(-${activeSlide.key * 100}%)`,
        }}
      >
        {map(slides, (slide, key) => (
          <li
            key={`${slide.url}-${key}`}
            onDoubleClick={handleDoubleClick}
            style={{
              display: 'flex',
              flexShrink: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          >
            {
              // eslint-disable-next-line no-nested-ternary
              includes(slide.tags, 'video')
                ? includes(slide.tags, 'vimeo')
                  ? (
                    <VimeoHorizontal
                      key={key}
                      asset={slide}
                      isActive={activeSlide.key === key}
                      isFullscreen={isFullscreen}
                      setting={productVideoAutoplay ? { autoplay: activeSlide.key === key } : undefined}
                    />
                  )
                  : (
                    <VideoHorizontal
                      asset={slide}
                      isActive={activeSlide.key === key}
                      isFullscreen={false}
                    />
                  )
                : <ImageHorizontal asset={slide} index={key} title={product?.name || ''} />
            }
          </li>
        ))}
      </ul>
    </>
  )

  return (
    <>
      <div
        className={clsx(
          className,
          styles.horizontalSliderRoot,
          isFullscreen && styles.fullscreen,
          autoHeight && styles.autoHeight,
        )}
      >
        {content(horizontalRef)}
      </div>
      {isSwipedUp && (
        <div className={clsx(className, styles.fullscreen, styles.swipe, styles.horizontalSliderRoot)}>
          {content(null)}
        </div>
      )}
    </>
  )
}

export default HorizontalSlider
